import { useState, useEffect } from 'react';
import { Copy, ExternalLink, Check } from 'react-feather';
import { agencyService } from '../../services/agency.service';
import { toast } from 'react-hot-toast';

interface ReferralLinkCardProps {
  referralCode: string;
  totalClicks?: number;
  conversions?: number;
}

export function ReferralLinkCard({
  referralCode,
  totalClicks = 0,
  conversions = 0,
}: ReferralLinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [referralUrl, setReferralUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (referralCode) {
      fetchReferralUrl();
    }
  }, [referralCode]);

  const fetchReferralUrl = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await agencyService.getBrandInstallationUrls();
      setReferralUrl(response.main_url);
    } catch (err: any) {
      console.error('Failed to fetch referral URL:', err);
      setError('Failed to load referral link');
      toast.error('Failed to load referral link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!referralUrl) {
      toast.error('No referral link available');
      return;
    }

    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleExternalLink = () => {
    if (referralUrl) {
      window.open(referralUrl, '_blank');
    }
  };

  return (
    <div className="bg-card rounded-lg shadow p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          My Referral Link
        </h3>
        <p className="text-sm text-muted-foreground">
          Share this link to refer new brands
        </p>
      </div>

      <div className="space-y-4">
        {/* Referral URL */}
        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          {isLoading ? (
            <div className="flex-1">
              <div className="h-4 bg-muted rounded animate-pulse"></div>
            </div>
          ) : error ? (
            <div className="flex-1">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          ) : (
            <>
              <span className="text-sm font-medium text-foreground flex-1 truncate">
                {referralUrl}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-accent rounded-lg transition-colors"
                  title="Copy link"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={handleExternalLink}
                  className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-accent rounded-lg transition-colors"
                  title="Open link"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {totalClicks}
            </div>
            <div className="text-sm text-blue-600 font-medium">
              Total Clicks
            </div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {conversions}
            </div>
            <div className="text-sm text-green-600 font-medium">
              Conversions
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 